import React, { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import type { DrillDownResponse, LoadDetail } from '../types';

interface DrillDownModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DrillDownResponse | null;
  isLoading: boolean;
  dateType: 'delivery' | 'pickup';
}

type SortField = keyof LoadDetail;
type SortDirection = 'asc' | 'desc';

const DrillDownModal: React.FC<DrillDownModalProps> = ({ isOpen, onClose, data, isLoading, dateType }) => {
  // Set default sort field based on dateType
  const defaultSortField = dateType === 'pickup' ? 'pickup_date' : 'delivery_date';
  const [sortField, setSortField] = useState<SortField>(defaultSortField);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const rowsPerPage = 7;

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!data?.loads) return [];

    let filtered = data.loads;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(load =>
        load.load_id.toLowerCase().includes(searchLower) ||
        load.dispatcher.toLowerCase().includes(searchLower) ||
        (load.driver && load.driver.toLowerCase().includes(searchLower)) ||
        (load.customer && load.customer.toLowerCase().includes(searchLower)) ||
        (load.equipment && load.equipment.toLowerCase().includes(searchLower))
      );
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle undefined values
      if (aVal === undefined || aVal === null) aVal = '';
      if (bVal === undefined || bVal === null) bVal = '';

      // Convert to comparable values
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [data?.loads, sortField, sortDirection, searchTerm]);
  
  // Calculate dynamic summary stats based on filtered data
  const dynamicSummary = useMemo(() => {
    if (!filteredAndSortedData.length) {
      return {
        load_count: 0,
        total_revenue: 0,
        total_miles: 0,
        weighted_rpm: 0
      };
    }
    
    const totalRevenue = filteredAndSortedData.reduce((sum, load) => 
      sum + (load.billable_revenue || 0), 0
    );
    const totalMiles = filteredAndSortedData.reduce((sum, load) => 
      sum + (load.total_miles || 0), 0
    );
    const weightedRpm = totalMiles > 0 ? totalRevenue / totalMiles : 0;
    
    return {
      load_count: filteredAndSortedData.length,
      total_revenue: totalRevenue,
      total_miles: totalMiles,
      weighted_rpm: weightedRpm
    };
  }, [filteredAndSortedData]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = filteredAndSortedData.slice(startIndex, endIndex);

  // Format currency
  const formatCurrency = (value?: number) => {
    return value ? `$${value.toLocaleString()}` : '-';
  };

  // Format date
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'MM/dd/yyyy');
    } catch {
      return dateStr;
    }
  };

  // Update sort field when dateType changes (even while open)
  React.useEffect(() => {
    setSortField(dateType === 'pickup' ? 'pickup_date' : 'delivery_date');
  }, [dateType]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setCurrentPage(1);
      setSortDirection('desc');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-netflix-grey-900 rounded-lg shadow-2xl max-w-7xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-netflix-grey-700">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {data?.title || 'Load Details'}
              </h2>
              {data?.subtitle && (
                <p className="text-netflix-grey-400 mt-1">{data.subtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-netflix-grey-400 hover:text-white transition-colors p-2 rounded-full hover:bg-netflix-grey-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-netflix-red"></div>
              <span className="ml-4 text-netflix-grey-400">Loading load details...</span>
            </div>
          )}

          {/* Content */}
          {!isLoading && data && (
            <>
              {/* Dynamic Summary Stats */}
              <div className="p-6 border-b border-netflix-grey-700">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">
                      {dynamicSummary.load_count.toLocaleString()}
                    </div>
                    <div className="text-sm text-netflix-grey-400">
                      {searchTerm ? 'Filtered Loads' : 'Total Loads'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400">
                      {formatCurrency(dynamicSummary.total_revenue)}
                    </div>
                    <div className="text-sm text-netflix-grey-400">
                      {searchTerm ? 'Filtered Revenue' : 'Total Revenue'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">
                      {Math.round(dynamicSummary.total_miles).toLocaleString()}
                    </div>
                    <div className="text-sm text-netflix-grey-400">
                      {searchTerm ? 'Filtered Miles' : 'Total Miles'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-400">
                      ${dynamicSummary.weighted_rpm.toFixed(2)}
                    </div>
                    <div className="text-sm text-netflix-grey-400">
                      {searchTerm ? 'Filtered RPM' : 'Weighted RPM'}
                    </div>
                  </div>
                </div>
                
                {/* Search Context Indicator */}
                {searchTerm && (
                  <div className="mt-4 text-center">
                    <div className="inline-flex items-center space-x-2 bg-netflix-red/20 px-3 py-1 rounded-full">
                      <svg className="w-4 h-4 text-netflix-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span className="text-sm text-netflix-red font-medium">
                        Filtered by: "{searchTerm}"
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Search and Controls */}
              <div className="p-6 border-b border-netflix-grey-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search loads, dispatchers, drivers..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="bg-netflix-grey-700 text-white placeholder-netflix-grey-400 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-netflix-red w-80"
                      />
                      <svg className="w-5 h-5 text-netflix-grey-400 absolute right-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-sm text-netflix-grey-400">
                    {filteredAndSortedData.length} of {data.total_count} loads
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="flex-1 overflow-hidden">
                <div className="overflow-x-auto h-full">
                  <table className="w-full">
                    <thead className="bg-netflix-grey-800 sticky top-0">
                      <tr>
                        {[
                          { key: 'load_id' as SortField, label: 'Load ID' },
                          { key: 'dispatcher' as SortField, label: 'Dispatcher' },
                          { key: 'driver' as SortField, label: 'Driver' },
                          { key: (dateType === 'pickup' ? 'pickup_date' : 'delivery_date') as SortField, label: dateType === 'pickup' ? 'Pickup Date' : 'Delivery Date' },
                          { key: 'total_miles' as SortField, label: 'Miles' },
                          { key: 'billable_revenue' as SortField, label: 'Revenue' },
                          { key: 'rpm' as SortField, label: 'RPM' },
                          { key: 'equipment' as SortField, label: 'Equipment' },
                        ].map(({ key, label }) => (
                          <th
                            key={key}
                            className="px-6 py-4 text-left text-sm font-medium text-netflix-grey-300 cursor-pointer hover:bg-netflix-grey-700 transition-colors"
                            onClick={() => handleSort(key)}
                          >
                            <div className="flex items-center space-x-1">
                              <span>{label}</span>
                              {sortField === key && (
                                <span className="text-netflix-red">
                                  {sortDirection === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-netflix-grey-700">
                      {currentRows.map((load, index) => (
                        <tr key={`${load.load_id}-${index}`} className="hover:bg-netflix-grey-800 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-white">
                            {load.load_id}
                          </td>
                          <td className="px-6 py-4 text-sm text-netflix-grey-300">
                            {load.dispatcher}
                          </td>
                          <td className="px-6 py-4 text-sm text-netflix-grey-300">
                            {load.driver || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-netflix-grey-300">
                            {formatDate(dateType === 'pickup' ? load.pickup_date : load.delivery_date)}
                          </td>
                          <td className="px-6 py-4 text-sm text-netflix-grey-300">
                            {load.total_miles?.toLocaleString() || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-green-400">
                            {formatCurrency(load.billable_revenue)}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-yellow-400">
                            {load.rpm ? `$${load.rpm.toFixed(2)}` : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-netflix-grey-300">
                            {load.equipment || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Empty State */}
                  {currentRows.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-netflix-grey-400 text-lg">
                        {searchTerm ? 'No loads match your search criteria' : 'No load data available'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-6 border-t border-netflix-grey-700">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-netflix-grey-400">
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length} loads
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === 1
                            ? 'bg-netflix-grey-700 text-netflix-grey-500 cursor-not-allowed'
                            : 'bg-netflix-grey-700 text-white hover:bg-netflix-grey-600'
                        }`}
                      >
                        Previous
                      </button>
                      
                      {/* Page numbers */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                                pageNum === currentPage
                                  ? 'bg-netflix-red text-white'
                                  : 'bg-netflix-grey-700 text-white hover:bg-netflix-grey-600'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === totalPages
                            ? 'bg-netflix-grey-700 text-netflix-grey-500 cursor-not-allowed'
                            : 'bg-netflix-grey-700 text-white hover:bg-netflix-grey-600'
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrillDownModal;
